import React, { useState, useCallback, useEffect, useRef } from 'react'
import { Select, Spin, Tabs, message } from 'antd'
import { ArrowLeftOutlined, UserOutlined, FileTextOutlined, CheckCircleOutlined, ExperimentOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import instituteRequest from 'utils/instituteRequest'
import studentRequest from 'utils/studentRequest'
import adminRequest from 'utils/adminRequest'
import bulkAddStudentsToAssessment from 'utils/bulkAddStudentsToAssessment'
import EnhancedBulkUploadDrawer from '../CreateAssessment/Drawers/EnhancedBulkUploadDrawer'
import InfoCardsUpdate from 'components/InfoCardsUpdate/cardDetails'

import {
  SectionWrapper,
  TopSection,
  TopHeader,
  CardRowSpaceBetween,
} from '../../Style/style'

import UnifiedAssessmentTable from './UnifiedAssessmentTable'
import TpoStudentListTable from './TpoStudentListTable'

const { Option } = Select

// --- In-app persistence (NOT across browser refresh) -----------------------
// Keeps the list controls (passing year, assessment-type filter, column
// filters, last fetch params) when the user opens an assessment and comes
// back, and caches the schedules response briefly so navigating back within a
// short window doesn't refire the request. Module-level → cleared on full
// page reload, which is the desired scope.
const DASHBOARD_UI_STATE = {} // instituteId -> { selectedYear, selectedAssessmentFilter, filters, lastFetchParams }
const SCHEDULES_CACHE = new Map() // cacheKey -> { ts, data }
const SCHEDULES_CACHE_TTL = 12000 // ~12s

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 0;
    background: #F5F5F5;
    border-radius: 12px;
    padding: 0;
    box-shadow: none;
    width: 100% !important;
    margin: 0 !important;
  }
  
  .ant-tabs-tab {
     flex: 0 0 calc(100% / ${props => props.$tabCount || 1}) !important;
    max-width: calc(100% / ${props => props.$tabCount || 1}) !important;
    padding: 16px 0 !important;
    font-weight: 400 !important;
    font-size: 14px !important;
    color: #000 !important;
    text-align: center;
    white-space: normal;
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box !important;
    transition: none !important;
    border: none !important;
    margin: 0 !important;
    
    &:hover {
      color: #3D5AFE;
      background: transparent;
    }
    
    &:first-child {
      border-top-left-radius: 12px;
      border-bottom-left-radius: 12px;
    }
    
    &:last-child {
      border-top-right-radius: 12px;
      border-bottom-right-radius: 12px;
    }
    
    &.ant-tabs-tab-active {
      background: #3D5AFE !important;
      border-radius: 12px;
      padding: 16px 0 !important;
      
      &:first-child {
        border-top-left-radius: 12px;
        border-bottom-left-radius: 12px;
      }
      
      &:last-child {
        border-top-right-radius: 12px;
        border-bottom-right-radius: 12px;
      }
      
      .ant-tabs-tab-btn {
        color: #FFFFFF !important;
        font-weight: 700 !important;
      }
      
      &:hover {
        background: #3D5AFE !important;
        
        .ant-tabs-tab-btn {
          color: #FFFFFF !important;
        }
      }
    }
  }
  
  .ant-tabs-ink-bar {
    display: none;
  }
  
  .ant-tabs-nav::before {
    border-bottom: none;
  }
  
  .ant-tabs-nav-wrap {
    padding: 0 !important;
    width: 100% !important;
  }
  
  .ant-tabs-nav-list {
    gap: 0;
    width: 100% !important;
    flex: 1;
    margin: 0 !important;  // Remove any default margins
  }
  
  // Ensure no extra spacing on the container
  .ant-tabs-content-holder {
    margin: 0 !important;
    padding: 0 !important;
  }
  
  .ant-tabs-nav-operations {
    display: none !important;
  }
`

const TabWrapper = styled.div`
  width: 100%;
  padding: 0 20px 0 0;
  margin-bottom: 24px;
  min-height: 52px;
`

const BackButton = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-right: 12px;
  padding: 4px;

  &:hover {
    color: #3249D7;
  }
`

const InstituteAssessmentDashboard = ({ institute, onBack, onAssessmentClick, onDiagnosisClick }) => {
  const instituteId = institute?.institute_id || institute?.id
  const instituteCampusId = institute?.instituteCampus?.[0]?.id || institute?.id
  const instituteName = institute?.instituteCampus?.[0]?.campusName || institute?.name || 'Institute'

  // Restore any list state persisted from a previous visit (in-app only).
  const persistedState = DASHBOARD_UI_STATE[instituteId] || {}

  const [loading, setLoading] = useState(false)
  const [activeAssessments, setActiveAssessments] = useState([])
  const [completedAssessments, setCompletedAssessments] = useState([])
  const [activePagination, setActivePagination] = useState({ page: 1, limit: 10, totalItems: 0 })
  const [completedPagination, setCompletedPagination] = useState({ page: 1, limit: 10, totalItems: 0 })
  // Remember the exact params of the last fetch so post-update refreshes stay on the same page/filter.
  const lastFetchParamsRef = useRef(persistedState.lastFetchParams || {})
  const [filters, setFilters] = useState(persistedState.filters || {})
  const [yearData, setYearData] = useState([])
  const [selectedYear, setSelectedYear] = useState(persistedState.selectedYear || '')
  const [selectedAssessmentFilter, setSelectedAssessmentFilter] = useState(persistedState.selectedAssessmentFilter || 'all')
  const [tpoStudentModalVisible, setTpoStudentModalVisible] = useState(false)
  const [assessmentTypes, setAssessmentTypes] = useState([])
  const [kpiData, setKpiData] = useState({
    totalCandidates: 0,
    totalAssessmentsSent: 0,
    totalAssessmentsTaken: 0,
    totalPracticeTests: 0,
  })

  const [addCandidateDrawerVisible, setAddCandidateDrawerVisible] = useState(false)
  const [selectedAssessmentForAdd, setSelectedAssessmentForAdd] = useState(null)
  const [addCandidateLoading, setAddCandidateLoading] = useState(false)
  const [yearLoaded, setYearLoaded] = useState(false)
  const [assessmentTypesLoaded, setAssessmentTypesLoaded] = useState(false)

  // Fetch year list and set default year before marking as loaded
  const fetchYearList = useCallback(async () => {
    if (!instituteCampusId) return
    try {
      const response = await studentRequest.get(`/students/institutes/${instituteCampusId}/yearOfPassing`)
      const years = response?.data?.data || []
      setYearData(years)
      // Don't override a year restored from a previous in-app visit.
      if (years.length > 0 && !DASHBOARD_UI_STATE[instituteId]?.selectedYear) {
        const currentYear = new Date().getFullYear()
        const hasCurrentYear = years.some(item => item.year == currentYear)
        if (hasCurrentYear) {
          setSelectedYear(currentYear.toString())
        } else {
          const yearValues = years.map(item => parseInt(item.year)).filter(y => !isNaN(y))
          if (yearValues.length > 0) setSelectedYear(Math.min(...yearValues).toString())
        }
      }
    } catch (error) {
      console.error('Error fetching year list:', error)
    } finally {
      setYearLoaded(true)
    }
  }, [instituteCampusId])

  // Fetch KPI states
  const fetchKPIStates = useCallback(async () => {
    if (!instituteId || !selectedYear) return
    try {
      const response = await studentRequest.get(`/students/assessments/getAssessmentStatesForInstitute/${instituteId}`, {
        params: { passingYear: selectedYear }
      })
      const data = response?.data?.data
      if (data) {
        setKpiData({
          totalCandidates: data.totalCandidates || 0,
          totalAssessmentsSent: data.totalAssessmentsSent || 0,
          totalAssessmentsTaken: data.totalAssessmentsTaken || 0,
          totalPracticeTests: data.totalPracticeTests || 0,
        })
      }
    } catch (error) {
      console.error('Error fetching KPI states:', error)
    }
  }, [instituteId, selectedYear])

  // Fetch assessment types dynamically
  const fetchAssessmentTypes = useCallback(async () => {
    if (!instituteId) return
    try {
      const response = await instituteRequest.get('/institutes/studentListInfo/getAssessmentTypes', {
        params: { instituteId },
      })
      const types = response?.data?.data || []
      setAssessmentTypes(types)
    } catch (error) {
      console.error('Error fetching assessment types:', error)
    } finally {
      setAssessmentTypesLoaded(true)
    }
  }, [instituteId])

  // Apply a schedules payload (from network or cache) to component state.
  const applySchedulesData = useCallback((data) => {
    const allSchedules = data?.data || []
    const active = allSchedules.filter(s => s.status !== 'Completed')
    const completed = allSchedules.filter(s => s.status === 'Completed')

    setActiveAssessments(active)
    setCompletedAssessments(completed)
    setActivePagination({
      page: (data?.pagination?.currentPage || 1),
      limit: data?.pagination?.pageSize || 10,
      totalItems: data?.pagination?.totalCount || 0,
    })
    setCompletedPagination({
      page: 1,
      limit: 10,
      totalItems: completed.length,
    })
  }, [])

  // Fetch assessments (active + completed combined)
  const fetchAssessments = useCallback(async (params = {}, options = {}) => {
    if (!instituteId || !selectedYear) return
    lastFetchParamsRef.current = params
    try {
      const typeNameMap = { 'all': '' }
      assessmentTypes.forEach(t => {
        const key = t.typeName?.toLowerCase() || t.key
        typeNameMap[key] = t.typeName || key
      })

      const requestParams = {
        entityType: 'college',
        instituteId,
        passingYear: selectedYear,
        order: 'createdAt',
        sort: 'desc',
        pageNo: params.pageNo || 0,
        pageLimit: params.pageLimit || 10,
        type_name: params.type_name !== undefined ? params.type_name : (typeNameMap[selectedAssessmentFilter] || ''),
        ...params,
      }

      // Short-lived cache: skip the request if we fetched this exact param set
      // within the TTL window (e.g. user navigated into an assessment and back).
      // `force` (used after mutations) always bypasses the cache.
      const cacheKey = JSON.stringify(requestParams)
      if (options.force) {
        // After a mutation, drop all cached pages so nothing stale is served.
        SCHEDULES_CACHE.clear()
      } else {
        const cached = SCHEDULES_CACHE.get(cacheKey)
        if (cached && (Date.now() - cached.ts) < SCHEDULES_CACHE_TTL) {
          applySchedulesData(cached.data)
          return
        }
      }

      setLoading(true)
      const response = await instituteRequest.get('/institutes/studentListInfo/getschedulesInfo', { params: requestParams })
      const data = response?.data?.data

      SCHEDULES_CACHE.set(cacheKey, { ts: Date.now(), data })
      applySchedulesData(data)
    } catch (error) {
      console.error('Error fetching assessments:', error)
      setActiveAssessments([])
      setCompletedAssessments([])
    } finally {
      setLoading(false)
    }
  }, [instituteId, selectedYear, selectedAssessmentFilter, assessmentTypes, applySchedulesData])

  // Persist list controls so they survive opening an assessment and coming back
  // (in-app only; cleared on full page reload).
  useEffect(() => {
    if (!instituteId) return
    DASHBOARD_UI_STATE[instituteId] = {
      selectedYear,
      selectedAssessmentFilter,
      filters,
      lastFetchParams: lastFetchParamsRef.current,
    }
  }, [instituteId, selectedYear, selectedAssessmentFilter, filters, activePagination])

  // Reset yearLoaded and fetch year list when institute changes
  useEffect(() => {
    if (instituteCampusId) {
      setYearLoaded(false)
      setAssessmentTypesLoaded(false)
      fetchYearList()
    }
  }, [instituteCampusId])

  useEffect(() => {
    if (instituteId && yearLoaded && selectedYear) {
      fetchKPIStates()
      fetchAssessmentTypes()
    }
  }, [instituteId, selectedYear, yearLoaded, fetchKPIStates, fetchAssessmentTypes])

  useEffect(() => {
    if (instituteId && yearLoaded && selectedYear) fetchAssessments()
  }, [instituteId, selectedYear, selectedAssessmentFilter, yearLoaded, fetchAssessments])

  const handleMenuClick = (filterType) => {
    setSelectedAssessmentFilter(filterType)
  }

  const handleAssessmentClick = (assessment) => {
    // Check if this is a diagnosis click
    if (assessment?.isDiagnosis) {
      onDiagnosisClick({
        instituteId,
        instituteName,
        assessmentType: assessment.assessmentType,
        assessmentNumber: 1,
        passingYear: selectedYear,
        scheduleId: assessment.scheduleId,
        scheduleName: assessment.scheduleName,
        enabledSections: assessment.enabledSections || [],
        assessmentDetails: assessment.assessmentDetails || [],
      })
    } else {
      onAssessmentClick(assessment)
    }
  }

  const handleAddCandidate = useCallback((record) => {
    setSelectedAssessmentForAdd(record)
    setAddCandidateDrawerVisible(true)
  }, [])

  const handleAddCandidateSubmit = useCallback(async (candidates) => {
    if (!candidates || candidates.length === 0) {
      message.warning('No candidates to add')
      return
    }

    setAddCandidateLoading(true)
    const progressKey = 'add-candidates-progress'
    try {
      const bulkUploadData = candidates.map(c => ({
        first_name: c.firstName || c.first_name || '',
        last_name: c.lastName || c.last_name || '',
        email: c.email || c.primary_email || '',
        primary_email: c.email || c.primary_email || '',
        // Optional mobile for SMS OTP — the drawer captures it as `phone`.
        // Whitelisted explicitly: this payload is built key-by-key, so anything
        // not listed here never reaches admin-node and the candidate silently
        // gets an email-only OTP.
        mobile_number: c.phone || c.mobile_number || c.mobile || '',
        // Country code travels as its own field so the backend never has to
        // guess whether mobile_number already contains one.
        country_code: c.countryCode || c.country_code || '',
        ...(c.degreeObj || c.degree ? {
          degree: c.degreeObj || c.degree,
          department: c.departmentObj || c.department,
          specialization: c.specializationObj || c.specialization,
          passingYear: c.passingYear,
        } : {}),
      }))

      const basePayload = { entityType: 'college' }
      // Determine ID field: standalone/one-time use assessmentInstituteMapId, scheduled use scheduleId
      if (selectedAssessmentForAdd?.isStandalone || selectedAssessmentForAdd?.isOneTime || selectedAssessmentForAdd?.assessmentInstituteMapId) {
        basePayload.assessmentInstituteMapId = selectedAssessmentForAdd.assessmentInstituteMapId
      } else if (selectedAssessmentForAdd?.scheduleId) {
        basePayload.scheduleId = selectedAssessmentForAdd.scheduleId
      }

      const result = await bulkAddStudentsToAssessment({
        client: adminRequest,
        url: '/assessment/addStudentsToAssessment',
        basePayload,
        bulkUploadData,
        chunkSize: 20,
        onProgress: ({ chunkIndex, totalChunks, addedSoFar, done }) => {
          if (done) {
            message.destroy(progressKey)
            return
          }
          message.loading({
            content: `Adding candidates… batch ${Math.min(chunkIndex + 1, totalChunks)} of ${totalChunks} (${addedSoFar} added)`,
            key: progressKey,
            duration: 0,
          })
        },
      })

      const { addedCount, skippedCount, failedCount, chunkErrors } = result
      if (failedCount === 0) {
        message.success(`Added ${addedCount} candidate(s)${skippedCount > 0 ? `, ${skippedCount} duplicate(s) skipped` : ''}`)
      } else {
        const firstErr = chunkErrors[0]?.message || 'unknown error'
        message.warning(`Added ${addedCount}, skipped ${skippedCount}, ${failedCount} failed (${firstErr}). Re-upload the failed rows to retry.`)
      }

      setAddCandidateDrawerVisible(false)
      setSelectedAssessmentForAdd(null)
      // Refresh the table (mutation → bypass + clear cache)
      fetchAssessments(lastFetchParamsRef.current, { force: true })
    } catch (error) {
      message.destroy(progressKey)
      console.error('Error adding candidates:', error)
      message.error(error?.response?.data?.message || error?.message || 'Failed to add candidates')
    } finally {
      setAddCandidateLoading(false)
    }
  }, [selectedAssessmentForAdd, fetchAssessments])

  const kpiCards = [
    {
      text: 'Total Candidates',
      value: kpiData.totalCandidates,
      padding: '16px',
      borderColor: '#BFBFBF',
      isSelected: false,
      textColor: '#3249D7',
      isFirst: true,
      icon: <UserOutlined style={{ fontSize: 20, color: '#3249D7' }} />,
    },
    {
      text: 'Total Assmts. Sent',
      value: kpiData.totalAssessmentsSent,
      padding: '16px',
      borderColor: '#BFBFBF',
      isSelected: false,
      textColor: '#393939',
      isFirst: false,
      icon: <FileTextOutlined style={{ fontSize: 20, color: '#393939' }} />,
    },
    {
      text: 'Total Assmts. Taken',
      value: kpiData.totalAssessmentsTaken,
      padding: '16px',
      borderColor: '#BFBFBF',
      isSelected: false,
      textColor: '#393939',
      isFirst: false,
      icon: <CheckCircleOutlined style={{ fontSize: 20, color: '#393939' }} />,
    },
    {
      text: 'Total Practice Tests',
      value: kpiData.totalPracticeTests,
      padding: '16px',
      borderColor: '#BFBFBF',
      isSelected: false,
      textColor: '#393939',
      isFirst: false,
      icon: <ExperimentOutlined style={{ fontSize: 20, color: '#393939' }} />,
    },
  ]

  const assessmentMenuItems = [
    {
      key: 'all',
      label: <div style={{ textAlign: 'center', lineHeight: '1.6' }}><div>ALL</div></div>,
    },
    ...assessmentTypes.map(t => ({
      key: t.typeName?.toLowerCase() || t.key,
      label: (
        <div style={{ textAlign: 'center', lineHeight: '1.6' }}>
          <div>{t.typeName || t.label}</div>
        </div>
      ),
    })),
  ]

  const cardClickHandler = (kpi) => {
    if (kpi.text === 'Total Candidates') {
      setTpoStudentModalVisible(!tpoStudentModalVisible)
    }
  }

  return (
    <SectionWrapper column>
      <div style={{ display: tpoStudentModalVisible ? 'none' : 'block' }}>
          {/* Header with Back button, Title and Passing Year Dropdown */}
          <TopSection padding="10px 24px 10px 0px">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                marginBottom: '24px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <BackButton onClick={onBack}>
                  <ArrowLeftOutlined style={{ fontSize: 18 }} />
                </BackButton>
                <TopHeader>{instituteName}</TopHeader>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ marginRight: '12px', whiteSpace: 'nowrap', fontWeight: '600', color: '#000000' }}>
                  Passing year
                </div>
                <Select
                  value={selectedYear}
                  onChange={val => setSelectedYear(val)}
                  placeholder="Select passing year"
                  style={{ minWidth: '180px' }}
                  showSearch={false}
                >
                  {yearData.map(y => (
                    <Option key={y.year} value={y.year?.toString()}>
                      {y.year}
                    </Option>
                  ))}
                </Select>
              </div>
            </div>
          </TopSection>

          {/* KPI Cards */}
          <CardRowSpaceBetween spaceBetween>
            {kpiCards.map((kpi, index) => (
              <InfoCardsUpdate
                key={index}
                padding={kpi.padding}
                width={'100%'}
                height={'100px'}
                text=""
                IconText={kpi.text}
                value={kpi.value}
                borderColor={`1px solid ${kpi.borderColor}`}
                isClickable={false}
                isSelected={kpi.isSelected}
                backgroundColor={'#ffffff'}
                textColor={kpi.textColor}
                icon={kpi.icon}
                isFirst={kpi.isFirst}
                onClick={() => cardClickHandler(kpi)}
                showStudentDetails={tpoStudentModalVisible}
                style={{ cursor: kpi.isFirst ? 'pointer' : 'default' }}
              />
            ))}
          </CardRowSpaceBetween>

          {/* Pill-style Assessment Type Tabs */}
          <TabWrapper>
            {assessmentTypesLoaded && (
              <StyledTabs
                $tabCount={assessmentMenuItems.length}
                activeKey={selectedAssessmentFilter}
                onChange={handleMenuClick}
                items={assessmentMenuItems}
                size="large"
                type="card"
              />
            )}
          </TabWrapper>

          {/* Unified Assessment Table with expandable rows */}
          <UnifiedAssessmentTable
            activeAssessments={activeAssessments}
            completedAssessments={completedAssessments}
            loading={loading}
            onAssessmentClick={handleAssessmentClick}
            onAddCandidate={handleAddCandidate}
            onScheduleUpdated={() => fetchAssessments(lastFetchParamsRef.current, { force: true })}
            instituteId={instituteId}
            passingYear={selectedYear}
            filters={filters}
            activePage={activePagination.page}
            completedPage={completedPagination.page}
            activeLimit={activePagination.limit}
            completedLimit={completedPagination.limit}
            activeTotalItems={activePagination.totalItems}
            completedTotalItems={completedPagination.totalItems}
            onPageChange={(page) => {
              fetchAssessments({
                pageNo: page,
                pageLimit: activePagination.limit,
              })
            }}
            onFilterChange={(newFilters) => {
              setFilters(newFilters)
              fetchAssessments({
                ...newFilters,
                pageNo: 0,
                pageLimit: activePagination.limit,
              })
            }}
          />

          {/* Add Candidate Drawer */}
          {addCandidateDrawerVisible && (
            <EnhancedBulkUploadDrawer
              visible={addCandidateDrawerVisible}
              onClose={() => {
                setAddCandidateDrawerVisible(false)
                setSelectedAssessmentForAdd(null)
              }}
              assessmentData={{
                qualificationData: [],
                instituteCampusId: instituteCampusId,
                instituteId: instituteId,
                /* Individual (one-time / standalone) assessments only. A
                   scheduled row is one generated RUN of a schedule — its dates
                   come from the schedule's frequency and validity, and the
                   next run would overwrite anything set here, so the end date
                   for those is edited on the schedule itself (Edit Schedule).
                   Passing no date simply hides the banner. */
                assessmentEndDate:
                  selectedAssessmentForAdd?.isOneTime ||
                  selectedAssessmentForAdd?.isStandalone
                    ? selectedAssessmentForAdd?.endDate
                    : undefined,
                assessmentStartDate: selectedAssessmentForAdd?.startDate,
                assessmentInstituteMapId: selectedAssessmentForAdd?.assessmentInstituteMapId,
                onEndDateUpdated: () =>
                  fetchAssessments(lastFetchParamsRef.current, { force: true }),
                preservedCandidates: [],
                preservedUploadSummaries: [],
                timestamp: Date.now(),
              }}
              entityType="college"
              mode="addCandidate"
              onDataSubmit={(candidates) => {
                handleAddCandidateSubmit(candidates)
              }}
            />
          )}
      </div>

      {/* TpoStudentListTable - shown when "Total Candidates" clicked */}
      {tpoStudentModalVisible && (
        <TpoStudentListTable
          instituteId={instituteId}
          onBack={() => setTpoStudentModalVisible(false)}
          selectedYear={selectedYear}
        />
      )}
    </SectionWrapper>
  )
}

export default InstituteAssessmentDashboard
